import { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { Popover, PopoverContent, PopoverItem, PopoverTrigger } from "@shared/v2/Popover";
import Alert from "@shared/v2/Alert";
import Error from "@shared/Error";
import TextButton from "@shared/v2/TextButton";
import Button from "@shared/v2/Button/Button";
import { ArrowUpRightSolidV6, Check, PlusSolidV6, Spinner } from "@shared/v2/Icomoon/Icons";
import { getTitleAndEscrow, getListingStage } from "./actions/thunks";
import { useFeatureFlags } from "../../../reducers/layoutReducer/selectors";
import { SubmitReqState } from "./actions/creators";
import errorShape from "./model/error";
import Header from "../../UI/Header";
import TitleAndEscrowCard from "./TitleAndEscrowCard";
import PSABlueprintModal from "./PSABlueprintModal";
import PreContractBlueprintModal from "./PreContractBlueprintModal";
import BlueprintStage from "./BlueprintStage";
import stageShape, { CTA_BEHAVIOR, stageBadge, stageCtaBehavior, stageStatusLabel } from "./model/stage";

const TitleAndEscrow = ({
  error,
  toastState,
  onSubmitReqState,
  type,
  isDismiss,
  isCardLoading,
  isStageLoading,
  getTitleAndEscrow,
  getListingStage,
  listingId,
  title,
  useCustomTitle,
  streetAddress,
  city,
  locality,
  postalCode,
  lastSubmitted,
  titleType,
  stage,
}) => {
  const [isPSAModalOpen, setPSAModalOpen] = useState(false);
  const [isPreContractModalOpen, setPreContractModalOpen] = useState(false);
  const featureFlags = useFeatureFlags();

  const checkFullAddress = (useCustomTitle ? title : streetAddress) && city && locality && postalCode;

  // Stages ship behind their own flag, independent of the one gating the rest of the card.
  // Pre-contract draws the same ladder from its own three rows, behind a second flag so it can be
  // turned off per account without a deploy.
  const isPreContract = titleType === "pre-contract";
  const isStageInScope =
    Boolean(featureFlags?.blueprintTitleStages) &&
    (!isPreContract || Boolean(featureFlags?.blueprintTitlePreContract));
  const activeStage = isStageInScope ? stage : null;
  const statusLabel = stageStatusLabel(activeStage);
  const ctaBehavior = stageCtaBehavior(activeStage);
  const isCtaDisabledByStage = ctaBehavior === CTA_BEHAVIOR.DISABLED;

  // The effect below fires getListingStage unconditionally, so the stage flag only counts as
  // loading for the accounts the stage feature actually applies to.
  const isLoading = isCardLoading || (isStageInScope && isStageLoading);

  useEffect(() => {
    getTitleAndEscrow(listingId);
    getListingStage(listingId);
  }, [listingId]);

  const showModal = () => {
    onSubmitReqState({ isloading: false, isSucceed: false, isFailed: false, failureMsg: "" });
    setPSAModalOpen(true);
  };

  const onHideHandlr = () => {
    onSubmitReqState({ isloading: false, isSucceed: false, isFailed: false, failureMsg: "" });
    setPSAModalOpen(false);
  };

  const showPreContractModal = () => {
    onSubmitReqState({ isloading: false, isSucceed: false, isFailed: false, failureMsg: "" });
    setPreContractModalOpen(true);
  };

  const onHidePreContractHandlr = () => {
    onSubmitReqState({ isloading: false, isSucceed: false, isFailed: false, failureMsg: "" });
    setPreContractModalOpen(false);
  };
  const canOpenTitle =
    checkFullAddress && featureFlags?.blueprintTitle110 && type === "seller" && !isCtaDisabledByStage;
  const badgeConfig = stageBadge(activeStage);
  let badgeElement = null;
  if (badgeConfig) {
    badgeElement = (
      <span
        className={`tw-ml-auto tw-pt-[6px] tw-text-[11px] tw-font-bold tw-uppercase tw-leading-[16px] ${badgeConfig.className}`}
      >
        {badgeConfig.label}
      </span>
    );
  }

  const buttonSchema = isPSAModalOpen || isPreContractModalOpen ? "primary" : "secondary";
  const buttonClass =
    "tw-mt-[4px] tw-w-full tw-h-[44px] !tw-rounded-[8px] tw-flex !tw-gap-[4px] tw-items-center tw-justify-center tw-text-14d !tw-normal-case";

  let metadataRow = null;
  if (statusLabel && lastSubmitted) {
    metadataRow = (
      <div className="tw-flex tw-justify-between tw-text-12px tw-font-normal tw-italic tw-leading-[16px] tw-text-gray-50">
        <span>Last Submitted:</span>
        <span>
          {statusLabel} {lastSubmitted}
        </span>
      </div>
    );
  } else if (!activeStage && lastSubmitted) {
    metadataRow = (
      <div className="tw-text-center tw-px-[8px] tw-text-12px tw-leading-[16px] tw-font-normal tw-text-gray-75">
        {isPreContract ? "Pre-contract Submitted:" : "Last Submitted:"} {lastSubmitted}
      </div>
    );
  }

  let ctaButton;
  if (isPreContract && isCtaDisabledByStage) {
    ctaButton = (
      <Button schema={buttonSchema} disabled onClick={showModal} className={buttonClass}>
        <PlusSolidV6 />
        <span>Submit Purchase Agreement</span>
      </Button>
    );
  } else if (canOpenTitle && isPreContract) {
    ctaButton = (
      <Button schema={buttonSchema} onClick={showModal} className={buttonClass}>
        <PlusSolidV6 />
        <span>Submit Purchase Agreement</span>
      </Button>
    );
  } else if (canOpenTitle) {
    ctaButton = (
      <Popover placement="bottom-end">
        <PopoverTrigger>
          <Button schema={buttonSchema} className={buttonClass}>
            <PlusSolidV6 />
            <span>Open Title with Blueprint</span>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="tw-w-[245px]">
          <PopoverItem onClick={showModal}>Purchase and Sale Agreement</PopoverItem>
          <PopoverItem onClick={showPreContractModal}>New Listing (Pre-contract) </PopoverItem>
        </PopoverContent>
      </Popover>
    );
  } else {
    ctaButton = (
      <Button
        schema={buttonSchema}
        disabled={!checkFullAddress || !featureFlags?.blueprintTitle110 || isCtaDisabledByStage}
        onClick={showModal}
        className={buttonClass}
      >
        <PlusSolidV6 />
        <span>Open Title with Blueprint</span>
      </Button>
    );
  }

  let cardBody;
  if (isLoading) {
    cardBody = (
      <div
        className="tw-flex tw-min-h-[200px] tw-items-center tw-justify-center"
        role="status"
        aria-label="Loading title status"
      >
        <Spinner size="xxxl" className="tw-animate-spin tw-text-neutral-gray-50" />
      </div>
    );
  } else {
    cardBody = (
      <>
        {error && <Error errorMessage={error.message} />}
        {!isDismiss && (!activeStage || statusLabel) && (
          <TitleAndEscrowCard isBlueprintOnboarded={featureFlags?.blueprintTitle110} />
        )}
        {activeStage && !statusLabel && <BlueprintStage stage={activeStage} isPreContract={isPreContract} />}
        {ctaBehavior !== CTA_BEHAVIOR.HIDDEN && <div>{ctaButton}</div>}
        {featureFlags?.blueprintTitle110 && !checkFullAddress && (
          <div className="tw-text-center tw-px-[8px] tw-text-12px tw-leading-[16px] tw-font-normal tw-text-gray-75">
            Please enter a full address for this transaction to start the title process
          </div>
        )}
        {featureFlags?.blueprintTitle110 && checkFullAddress && metadataRow}
      </>
    );
  }

  return (
    <>
      {toastState?.show && toastState?.msg && (
        <Alert
          variant="success"
          containerClass="tw-z-1000 !tw-top-[100px] !tw-fixed !tw-right-3/12 !tw-left-4/12"
          text={
            <>
              <Check size="xl" className="tw-w-16px tw-h-16px tw-mb-[-4px]" />
              <span className="tw-capitalize tw-pl-[9px]">{toastState?.msg}</span>
            </>
          }
          role="alert"
        />
      )}

      <div className="tw-flex tw-flex-col tw-gap-[16px]">
        <Header header="Title and Escrow" add={badgeElement} />
        {cardBody}
        {featureFlags?.blueprintTitle110 && (
          <div className="tw-text-center">
            <TextButton size="small" onClick={() => window.open("https://app.blueprinttitle.com", "_blank")}>
              <span className="tw-pr-[4px]">Go to Blueprint Portal</span>
              <ArrowUpRightSolidV6 size="s" />
            </TextButton>
          </div>
        )}
      </div>
      {isPSAModalOpen && <PSABlueprintModal isModalOpen={isPSAModalOpen} onHideHandlr={onHideHandlr} />}
      {isPreContractModalOpen && (
        <PreContractBlueprintModal
          isModalOpen={isPreContractModalOpen}
          onHideHandlr={onHidePreContractHandlr}
        />
      )}
    </>
  );
};

TitleAndEscrow.propTypes = {
  error: errorShape,
  isDismiss: PropTypes.bool,
  isCardLoading: PropTypes.bool,
  isStageLoading: PropTypes.bool,
  getTitleAndEscrow: PropTypes.func,
  getListingStage: PropTypes.func,
  listingId: PropTypes.number.isRequired,

  type: PropTypes.string,
  toastState: PropTypes.shape({
    show: PropTypes.bool,
    msg: PropTypes.string,
  }),
  onSubmitReqState: PropTypes.func.isRequired,
  streetAddress: PropTypes.string,
  title: PropTypes.string,
  useCustomTitle: PropTypes.bool,
  city: PropTypes.string,
  locality: PropTypes.string,
  postalCode: PropTypes.string,
  lastSubmitted: PropTypes.string,
  titleType: PropTypes.string,
  stage: stageShape,
};

TitleAndEscrow.defaultProps = {
  error: null,
  getTitleAndEscrow: null,
  getListingStage: null,
  isDismiss: false,
  isCardLoading: false,
  isStageLoading: false,
  type: null,
  toastState: {
    show: false,
    msg: "",
  },
  streetAddress: "",
  title: "",
  useCustomTitle: false,
  city: "",
  locality: "",
  postalCode: "",
  lastSubmitted: "",
  titleType: "",
  stage: null,
};

const mapStateToProps = (state) => ({
  error: state.tdpTitleAndEscrowReducer.error,
  toastState: state.tdpTitleAndEscrowReducer.toastState,
  type: state.tdpDetailsReducer.headerDetails.type,
  isDismiss: state.tdpTitleAndEscrowReducer.isDismiss,
  isCardLoading: state.tdpTitleAndEscrowReducer.isCardLoading,
  isStageLoading: state.tdpTitleAndEscrowReducer.isStageLoading,
  listingId: state.tdpDetailsReducer.headerDetails.listing_id,
  title: state.tdpDetailsReducer.transactionDetails.title,
  useCustomTitle: state.tdpDetailsReducer.transactionDetails.use_custom_title,
  streetAddress: state.tdpDetailsReducer.propertyDetails.street_address,
  city: state.tdpDetailsReducer.propertyDetails.city,
  locality: state.tdpDetailsReducer.propertyDetails.state,
  postalCode: state.tdpDetailsReducer.propertyDetails.zip,
  lastSubmitted: state.tdpTitleAndEscrowReducer.lastSubmitted,
  titleType: state.tdpTitleAndEscrowReducer.titleType,
  stage: state.tdpTitleAndEscrowReducer.stage,
});
const mapDispatchToProps = (dispatch) => ({
  onSubmitReqState: (data) => dispatch(SubmitReqState(data)),
  getTitleAndEscrow: (listingId) => dispatch(getTitleAndEscrow(listingId)),
  getListingStage: (listingId) => dispatch(getListingStage(listingId)),
});
export default connect(mapStateToProps, mapDispatchToProps)(TitleAndEscrow);
